import { WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR } from "@/constants";
import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import { globalProductIdManagerToken } from "@/tokens";
import { ReviewsProductPageWidgetDefinition } from "@apps/reviews/definitions";
import { ProductIdFromStaticProvider } from "@lib/ProductIdManager/ProductIdFromStaticProvider";
import { ProductIdManager } from "@lib/ProductIdManager/ProductIdManager";
import AnalyticsEventsCollectorInterface from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsCollectorInterface";
import { AnalyticsEventsFactory } from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsFactory";
import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import {
    parseIntDataAttr,
    parseIntFromGidDataAttr,
    parseOptionalBooleanDataAttr,
} from "@ui-utils/Injector/utils";
import { container } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";

const OLD_REVIEW_PRODUCT_ID = "product_id";
const REVIEW_PRODUCT_ID = "gw-product-id";
const MIN_IMAGES_FOR_SLIDER_VISIIBILITY = "gw-min-images-for-slider-visibility";
const REVIEWS_PER_PAGE = "gw-reviews-per-page";

const MIN_IMAGES_FOR_SLIDER_VISIIBILITY_DEFAULT = 6;
const REVIEWS_PER_PAGE_DEFAULT = 5;

class ReviewsProductPageWidgetInjector {
    constructor(
        private readonly placeholderSelector: string,
        private readonly logger: LoggerInterface,
        private readonly eventsCollector: AnalyticsEventsCollectorInterface,
        private readonly analyticsEventsFactory: AnalyticsEventsFactory,
        private readonly dynamicImportManager: DynamicImportManager,
        private readonly moi: MutationObserverInjector
    ) {}

    start(): void {
        this.logger.debug("ReviewsProductPageWidgetInjector start", {
            placeholderSelector: this.placeholderSelector,
        });

        const targets$ = this.moi.register({
            targetSelector: this.placeholderSelector,
        });

        // Временное решение. Как только закончим ScrollManager и скролл между виджетами, поменять this.moi.targets$ на ...  this.ioi.targets$
        targets$.subscribe((placeholder) => {
            let productIdFromDataAttr =
                parseIntDataAttr(placeholder, REVIEW_PRODUCT_ID) ||
                parseIntFromGidDataAttr(placeholder, REVIEW_PRODUCT_ID);

            const minImagesForSliderVisibilityFromDataAttr =
                parseIntDataAttr(
                    placeholder,
                    MIN_IMAGES_FOR_SLIDER_VISIIBILITY
                ) || MIN_IMAGES_FOR_SLIDER_VISIIBILITY_DEFAULT;

            const reviewsPerPageFromDataAttr =
                parseIntDataAttr(placeholder, REVIEWS_PER_PAGE) ||
                REVIEWS_PER_PAGE_DEFAULT;

            this.logger?.debug(
                "ReviewsProductPageWidgetInjector found placeholder",
                {
                    placeholder,
                    productIdFromDataAttr,
                    minImagesForSliderVisibilityFromDataAttr,
                    reviewsPerPageFromDataAttr,
                }
            );

            if (productIdFromDataAttr === null) {
                productIdFromDataAttr = parseIntDataAttr(
                    placeholder,
                    OLD_REVIEW_PRODUCT_ID
                );

                if (productIdFromDataAttr !== null) {
                    this.eventsCollector.pushEvent(
                        this.analyticsEventsFactory.createWidgetInjectedWithAttribute(
                            REVIEWS_WIDGET_NAMES.REVIEWS_PRODUCT_PAGE_WIDGET,
                            OLD_REVIEW_PRODUCT_ID
                        )
                    );
                }
            }

            if (productIdFromDataAttr === null) {
                this.logger.error(
                    `ReviewsProductPageWidget placeholder does not have data-${REVIEW_PRODUCT_ID} attribute.`
                );
            }

            const productIdManager =
                productIdFromDataAttr === null
                    ? container.resolve(globalProductIdManagerToken)
                    : new ProductIdManager();

            if (productIdFromDataAttr !== null) {
                productIdManager.registerProvider(
                    new ProductIdFromStaticProvider(productIdFromDataAttr)
                );
            }

            void (async () => {
                const ReviewsProductPageWidget =
                    await this.dynamicImportManager.importDefault(
                        ReviewsProductPageWidgetDefinition,
                        true
                    );

                const widget = new ReviewsProductPageWidget(
                    {
                        target: placeholder,
                        minImagesForSliderVisibility:
                            minImagesForSliderVisibilityFromDataAttr,
                        reviewsPerPage: reviewsPerPageFromDataAttr,
                        isWidgetShadowRootEnabled: parseOptionalBooleanDataAttr(
                            placeholder,
                            WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                        ),
                    },
                    this.logger,
                    productIdManager
                );

                await widget.init();

                this.eventsCollector.pushEvent(
                    this.analyticsEventsFactory.createWidgetInjected(
                        REVIEWS_WIDGET_NAMES.REVIEWS_PRODUCT_PAGE_WIDGET
                    )
                );

                dispatchSdkEvent(
                    new WidgetInjectedSdkEvent(
                        REVIEWS_WIDGET_NAMES.REVIEWS_PRODUCT_PAGE_WIDGET
                    )
                );
            })();
        });
    }
}

export default ReviewsProductPageWidgetInjector;
